import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrompts } from '../context/PromptContext';
import { useButtonState } from '../hooks/useButtonState';
import CollapsibleSection from './CollapsibleSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHash, FiPlus, FiTrash2, FiTag, FiX, FiArrowLeft, FiHeart, FiCheck } = FiIcons;

const VariablesManager = () => {
  const navigate = useNavigate();
  const { variables, rawVariables, addVariable, deleteVariable, addValueToVariable, removeValueFromVariable, toggleVariableFavouriteByKey } = usePrompts();
  const [newVarName, setNewVarName] = useState('');
  const [selectedVar, setSelectedVar] = useState(null);
  const [newValue, setNewValue] = useState('');
  const [deleteConfirmKey, setDeleteConfirmKey] = useState(null);
  const [loadingFavouriteKeys, setLoadingFavouriteKeys] = useState(new Set());
  const pendingFavouriteByKey = useRef(new Map());
  const latestRequestedFavouriteByKey = useRef(new Map());
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const {
    state: createVarState,
    error: createVarError,
    handleAction: handleCreateVarAction,
    reset: resetCreateVarState,
    icon: createVarIcon,
    text: createVarText,
    classes: createVarClasses,
  } = useButtonState({
    idleText: 'Create Variable',
    loadingText: 'Creating...',
    successText: 'Created!',
    failedText: 'Failed',
    idleIcon: FiPlus,
    onSuccess: () => {
      setSelectedVar(newVarName.trim());
    },
    onError: (error) => {
      console.error('Failed to create variable:', error);
    },
    autoResetDelay: 2000,
  });

  const variableKeys = Object.keys(variables);
  const selectedValues = selectedVar ? (variables[selectedVar] || []) : [];

  const getVariableFavourite = (key) => {
    const variable = rawVariables.find(v => v.key === key);
    return variable ? variable.favourite : false;
  };

  const getVariableIdsByKey = (key) => {
    return rawVariables.filter(v => v.key === key).map(v => v.id);
  };


  const handleCreateVariable = async () => {
    if (!newVarName.trim()) {
      resetCreateVarState();
      return;
    }

    await handleCreateVarAction(async () => {
      await addVariable(newVarName.trim(), [], true);
    });

    setNewVarName('');
  };

  const handleDeleteVariable = (key) => {
    if (deleteConfirmKey === key) {
      deleteVariable(key);
      if (selectedVar === key) setSelectedVar(null);
      setDeleteConfirmKey(null);
    } else {
      setDeleteConfirmKey(key);
    }
  };

  const enqueueFavouriteToggle = async (key) => {
    const currentFavourite = getVariableFavourite(key);
    const nextFavourite = !currentFavourite;
    latestRequestedFavouriteByKey.current.set(key, nextFavourite);

    if (pendingFavouriteByKey.current.has(key)) return;

    setLoadingFavouriteKeys(prev => new Set(prev).add(key));
    pendingFavouriteByKey.current.set(key, true);

    try {
      while (isMountedRef.current && pendingFavouriteByKey.current.has(key)) {
        const desiredFavourite = latestRequestedFavouriteByKey.current.get(key);
        if (desiredFavourite === undefined) break;

        await toggleVariableFavouriteByKey(key, desiredFavourite);

        if (latestRequestedFavouriteByKey.current.get(key) === desiredFavourite) {
          break;
        }
      }
    } catch (error) {
      console.error('Failed to toggle favourite:', error);
    } finally {
      pendingFavouriteByKey.current.delete(key);
      latestRequestedFavouriteByKey.current.delete(key);
      if (isMountedRef.current) {
        setLoadingFavouriteKeys(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    }
  };

  const handleToggleFavourite = (key) => {
    const variableIds = getVariableIdsByKey(key);
    if (variableIds.length === 0) return;
    enqueueFavouriteToggle(key);
  };

  const handleAddValue = () => {
    if (!selectedVar || !newValue.trim()) return;
    
    // Split by comma and process each value
    const valuesToAdd = newValue.split(',')
      .map(v => v.trim())
      .filter(v => v !== '');

    if (valuesToAdd.length === 0) return;

    // Add each value
    valuesToAdd.forEach(val => {
      addValueToVariable(selectedVar, val);
    });
    
    setNewValue('');
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter') action();
  };

  return (
    <div className="max-w-7xl mx-auto pb-12 px-4 sm:px-0 pb-bottom-nav">
      <div className="flex items-center gap-4 mb-8">
        <div className="p-2 border-2 border-black bg-white shadow-neo min-w-[44px] min-h-[44px] flex items-center justify-center">
          <SafeIcon icon={FiHash} size={32} />
        </div>
        <h1 className="text-2xl md:text-3xl font-black text-black uppercase">
          Variables Manager
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
        {/* Left Column: Variable List */}
        <div className="lg:col-span-1">
          <CollapsibleSection title="VARIABLE KEYS" headerColor="bg-neo-purple" defaultExpanded={true} className="min-h-[250px] md:min-h-[400px] md:h-[600px]">
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="New Variable Name..."
                  value={newVarName}
                  onChange={(e) => setNewVarName(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleCreateVariable)}
                  className="neo-input text-sm input-touch"
                />
                <button
                  onClick={handleCreateVariable}
                  className={`neo-btn-icon bg-black text-white px-3 min-w-[44px] min-h-[44px] touch-target-sm transition-all ${createVarClasses} ${
                    newVarName.trim()
                      ? 'hover:bg-neo-green hover:border-neo-green hover:cursor-pointer'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!newVarName.trim() || createVarState === 'loading'}
                >
                  {createVarIcon && <SafeIcon icon={createVarIcon} size={16} />}
                  <span className="sr-only">{createVarText}</span>
                </button>
                {createVarState === 'failed' && createVarError && (
                  <div className="absolute top-full right-0 mt-2 bg-red-100 border-2 border-red-600 p-2 text-xs text-red-800 font-bold max-w-xs z-50">
                    Variable name is required
                  </div>
                )}
              </div>

              <div className="overflow-y-auto overscroll-contain touch-pan-y space-y-2 max-h-[calc(600px-120px)] bg-gray-50">
                {variableKeys.length === 0 ? (
                  <p className="text-center text-gray-500 font-mono text-sm py-4">No variables defined.</p>
                ) : (
                  variableKeys.map(key => {
                    const isFavourite = getVariableFavourite(key);
                    const isConfirmingDelete = deleteConfirmKey === key;
                    const isLoadingFavourite = loadingFavouriteKeys.has(key);
                    return (
                      <div
                        key={key}
                        onClick={() => setSelectedVar(key)}
                        className={`p-3 border-2 border-black flex justify-between items-center cursor-pointer transition-all ${
                          selectedVar === key
                            ? 'bg-neo-yellow shadow-none translate-x-[2px] translate-y-[2px]'
                            : 'bg-white hover:bg-gray-100 shadow-neo-sm'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleFavourite(key); }}
                            disabled={isLoadingFavourite}
                            className={`p-2 min-w-[44px] min-h-[44px] rounded transition-all ${
                              isLoadingFavourite
                                ? 'opacity-50 cursor-not-allowed'
                                : isFavourite
                                  ? 'text-red-500 bg-red-50 border-2 border-red-400 shadow-neo-sm'
                                  : 'text-gray-400 border-2 border-transparent hover:border-red-300 hover:bg-red-50 hover:text-red-400'
                            }`}
                            title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                          >
                            <SafeIcon icon={FiHeart} size={16} className={isLoadingFavourite ? 'animate-pulse' : ''} />
                          </button>
                          <span className="font-bold uppercase text-sm truncate">{key}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-black text-white px-1.5 rounded-full font-mono">
                            {variables[key].length}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteVariable(key); }}
                            className={`p-2 min-w-[44px] min-h-[44px] rounded transition-colors ${
                              isConfirmingDelete
                                ? 'text-red-600 bg-red-100 hover:bg-red-200 cursor-pointer'
                                : 'hover:text-red-500 hover:bg-red-50'
                            }`}
                            title={isConfirmingDelete ? 'Click to confirm deletion' : 'Delete variable'}
                          >
                            {isConfirmingDelete ? (
                              <SafeIcon icon={FiCheck} size={14} />
                            ) : (
                              <SafeIcon icon={FiTrash2} size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </CollapsibleSection>
        </div>

        {/* Right Column: Values Editor */}
        <div className="lg:col-span-2">
          {selectedVar ? (
            <div className="neo-card p-0 h-full flex flex-col">
              <div className="neo-window-header bg-neo-green">
                <span>Values for: <span className="uppercase font-black ml-2">{selectedVar}</span></span>
              </div>

               <div className="p-6 bg-white border-b-2 border-black">
                 <label className="block text-xs font-bold text-black mb-2 uppercase">
                   Add New Value
                 </label>
                 <div className="flex gap-2">
                   <input
                     type="text"
                     placeholder={`Enter value for {${selectedVar}}...`}
                     value={newValue}
                     onChange={(e) => setNewValue(e.target.value)}
                     onKeyDown={(e) => handleKeyDown(e, handleAddValue)}
                     className="neo-input input-touch"
                   />
                   <button
                     onClick={handleAddValue}
                     className="neo-btn neo-btn-primary flex items-center whitespace-nowrap min-h-[44px] px-3"
                     disabled={!newValue.trim()}
                   >
                     <SafeIcon icon={FiPlus} className="sm:mr-2" size={16} />
                     <span className="hidden sm:inline">Add Value</span>
                   </button>
                 </div>
               </div>

               <div className="p-6 bg-gray-50 flex-1 overflow-y-auto">
                 <div className="flex flex-wrap gap-2 sm:gap-3">
                  {selectedValues.length === 0 ? (
                    <div className="w-full text-center py-10 text-gray-400">
                      <SafeIcon icon={FiTag} size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="font-mono text-sm">No values predefined for this variable.</p>
                    </div>
                  ) : (
                    selectedValues.map((val, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-white border-2 border-black px-3 py-2 shadow-neo-sm group hover:scale-105 transition-transform"
                      >
                        <span className="font-mono text-sm mr-3 font-bold">{val}</span>
                        <button
                          onClick={() => removeValueFromVariable(selectedVar, val)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <SafeIcon icon={FiX} size={16} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (

            <div className="h-full border-2 border-black border-dashed bg-gray-100 flex flex-col items-center justify-center text-gray-400 p-8">
              <SafeIcon icon={FiHash} size={64} className="mb-4 opacity-20" />
              <p className="text-xl font-bold uppercase mb-2">No Variable Selected</p>
              <p className="font-mono text-sm max-w-md text-center">
                Select a variable from list on left or create a new one to manage its predefined values.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VariablesManager;
